import * as React from 'react';
import styled from '@emotion/styled';
import { LightboxContext } from './LightboxWrapper';

export const Gallery: React.FC<React.PropsWithChildren<{ label?: string }>> = ({
  children,
  label = 'Image gallery'
}) => <S.Gallery aria-label={label}>{children}</S.Gallery>;

export const GalleryItem: React.FC<React.PropsWithChildren<{ caption?: string; zoom?: boolean }>> = ({
  children,
  caption,
  zoom = true
}) => {
  const lightbox = React.useContext(LightboxContext);
  const open = (element: HTMLElement) => {
    const image = element.querySelector('img');
    if (image) lightbox.setImage(image.src);
  };
  return (
    <S.Item>
      <S.Preview
        role={zoom ? 'button' : undefined}
        tabIndex={zoom ? 0 : undefined}
        aria-label={zoom ? `Enlarge: ${caption || 'image'}` : undefined}
        onClick={(event) => {
          if (!zoom) return;
          event.stopPropagation();
          open(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (!zoom) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          open(event.currentTarget);
        }}
      >
        {children}
      </S.Preview>
      {caption && <figcaption>{caption}</figcaption>}
    </S.Item>
  );
};

namespace S {
  export const Gallery = styled.section`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
    gap: 20px;
    margin: 24px 0;
  `;
  export const Item = styled.figure`
    min-width: 0;
    margin: 0;
    figcaption {
      margin-top: 12px;
      color: ${(p) => p.theme.text.paragraph};
      font-size: 13px;
      line-height: 1.5;
      text-align: center;
    }
  `;
  export const Preview = styled.div`
    cursor: default;
    &[role='button'] {
      cursor: zoom-in;
    }
    border-radius: var(--content-list-radius);
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.text.special};
      outline-offset: 4px;
    }
    && p {
      margin: 0;
    }
    && video,
    && img {
      display: block;
      width: auto;
      max-width: 100%;
      height: auto;
      max-height: 520px;
      margin: 0 auto;
      cursor: zoom-in;
      transform: none;
    }
  `;
}
